// Логика кабинета поставщика над api (ТЗ 4.2, 6.5): список запросов и карточка запроса, файлы КП с
// заданием сопоставления, черновики и отправленные отклики, отправка, отзыв, подтверждение, замена
// новым КП вместо подтверждения, применение сопоставления, чаты поставщика и его карточка.
//
// Все операции записи идут через общий механизм показа ошибки (model/apiRun): тексты 422 и 429
// показываются как есть (ТЗ 6.9), отказ — это `null` в ответе. Необратимые действия спрашивают
// подтверждение (ТЗ 6.9). Сгенерировано Claude.
import popup from '@/model/popup';
import { apiRun } from '@/model/apiRun';
import specRequestApi from '@/api/specRequest';
import specOfferApi from '@/api/specOffer';
import specQuoteFileApi from '@/api/specQuoteFile';
import specAiJobApi from '@/api/specAiJob';
import specChatApi from '@/api/specChat';
import supplierApi from '@/api/supplier';
import fileApi from '@/api/file';

/** Интервал опроса задания сопоставления КП (ТЗ 5.1: раз в 3 секунды). */
export const POLL_INTERVAL = 3000;

/** Состояния задания AI, при которых опрос продолжается (ТЗ 5.1). */
export const JOB_ACTIVE = ['queued', 'processing'];

export default {
  // ==== запросы ====

  /** «Мои запросы»: только адресованные этому поставщику (ТЗ 4.2, 3.4). */
  fetchRequests(withPreloader = true) {
    return specRequestApi.fetchMine(withPreloader).then((r) => r.items ?? []);
  },

  /** Карточка запроса; первое открытие переводит запрос в «Просмотрен» (ТЗ 4.2, 2.7). */
  fetchCard(requestId, withPreloader = true) {
    return specRequestApi.fetchItem(requestId, {}, withPreloader);
  },

  // ==== файлы КП ====

  /**
   * Загрузка КП: общая загрузка файла с назначением «спецификация», затем привязка к запросу
   * (ТЗ 4.2, 9.1). `replacedItemId` — загрузка «вместо подтверждения» по выбранному отклику этой
   * позиции (ТЗ 2.8): сервер отзывает отклик и возвращает позицию к выбору.
   */
  async uploadQuote(requestId, file, replacedItemId = null) {
    const uploaded = await apiRun(
      () => fileApi.createForSpecification(file),
      'Не удалось загрузить файл',
    );
    if (!uploaded) return null;

    const params = {
      request: `/api/spec_requests/${requestId}`,
      file: `/api/files/${uploaded.id}`,
    };
    if (replacedItemId) params.replacedItem = `/api/spec_items/${replacedItemId}`;

    return apiRun(
      () => specQuoteFileApi.create(params),
      'Не удалось привязать файл КП к запросу',
    );
  },

  /** Отменить сопоставление КП, пока оно в работе (ТЗ 4.1 «Отменить задание»); ответ — задание. */
  cancelMatch(jobId) {
    return apiRun(
      () => specAiJobApi.cancel(jobId),
      'Не удалось отменить сопоставление',
    );
  },

  /** «Загрузить новое КП вместо подтверждения» — необратимо, поэтому с подтверждением (ТЗ 6.9). */
  async replaceByQuote(requestId, item, file) {
    const answer = await popup.addConfirm(
      `Загрузить новое КП вместо подтверждения по позиции «${item.name}»?`,
      'Выбранный отклик будет отозван, позиция вернётся к выбору отклика, скидка заказчику обнулится.',
    );
    if (!answer) return null;
    return this.uploadQuote(requestId, file, item.id);
  },

  /** Файл КП с заданием сопоставления — для экрана проверки (ТЗ 4.2, 6.5). */
  fetchQuoteFile(quoteFileId, withPreloader = true) {
    return specQuoteFileApi.fetchItem(quoteFileId, {}, withPreloader);
  },

  /**
   * Задание сопоставления с результатом — для экрана проверки (ТЗ 4.2, 5.5). В карточке запроса
   * задание отдельно не читается: состояние и текст ошибки есть в самом файле КП (ТЗ 4.2).
   */
  fetchJob(jobId) {
    return specAiJobApi.fetchItem(jobId);
  },

  /** Применить проверенное сопоставление: строки → черновики откликов (ТЗ 4.2, 5.5). */
  applyMatch(jobId, rows) {
    return apiRun(
      () => specAiJobApi.applyMatch(jobId, rows, true),
      'Не удалось сформировать отклики',
    );
  },

  // ==== отклики ====

  /** Черновик отклика вручную (ТЗ 4.2, 6.5 «+ Отклик вручную»). */
  createOffer(requestId, itemId, fields) {
    return apiRun(
      () => specOfferApi.create({
        request: `/api/spec_requests/${requestId}`,
        item: `/api/spec_items/${itemId}`,
        ...fields,
      }),
      'Не удалось создать отклик',
    );
  },

  /**
   * Правка черновика или отправленного отклика (ТЗ 4.2). У отправленного каждое изменённое поле
   * пишется в историю, отклик помечается «изменён», дизайнеру уходит уведомление; причина
   * необязательна.
   */
  updateOffer(offerId, fields) {
    return apiRun(
      () => specOfferApi.update(offerId, fields),
      'Не удалось сохранить отклик',
    );
  },

  /** Удаление черновика — необратимо, поэтому с подтверждением (ТЗ 6.9). */
  async deleteOffer(offer) {
    const answer = await popup.addConfirm(
      'Удалить черновик отклика?',
      'Черновик исчезнет без следа: дизайнер его не видел.',
    );
    if (!answer) return false;

    const result = await apiRun(
      () => specOfferApi.delete(offer.id),
      'Не удалось удалить черновик',
    );
    return result !== null;
  },

  /** Отправка откликов — необратима, поэтому с подтверждением (ТЗ 6.9). */
  async sendOffers(requestId, count) {
    const answer = await popup.addConfirm(
      `Отправить отклики (${count})?`,
      'Дизайнер увидит отклики и получит уведомление.',
    );
    if (!answer) return null;

    return apiRun(
      () => specRequestApi.sendOffers(requestId, null, true),
      'Не удалось отправить отклики',
    );
  },

  /** Отзыв отклика — необратим, поэтому с подтверждением (ТЗ 6.9, условия — 2.5). */
  async withdrawOffer(offer) {
    const answer = await popup.addConfirm(
      'Отозвать отклик?',
      'Если отклик был выбран, позиция вернётся к выбору отклика, а скидка заказчику обнулится.',
    );
    if (!answer) return null;

    return apiRun(
      () => specOfferApi.withdraw(offer.id),
      'Не удалось отозвать отклик',
    );
  },

  /** Подтверждение отклика — необратимо, поэтому с подтверждением (ТЗ 6.9). */
  async confirmOffer(offer, itemName) {
    const answer = await popup.addConfirm(
      `Подтвердить отклик по позиции «${itemName}»?`,
      'Позиция перейдёт в статус «Согласовано поставщиком».',
    );
    if (!answer) return null;

    return apiRun(
      () => specOfferApi.confirm(offer.id),
      'Не удалось подтвердить отклик',
    );
  },

  /** История одного отклика (ТЗ 4.2, 6.5). */
  fetchOfferHistory(offerId) {
    return specOfferApi.fetchHistory(offerId).then((r) => r.items ?? []);
  },

  // ==== карточка поставщика ====

  fetchProfile(supplierId) {
    return supplierApi.fetchItem(supplierId, {}, true);
  },

  /** Правка контактов своей карточки; остальные поля сервер отклоняет с 422 (ТЗ 4.2). */
  saveContacts(supplierId, contacts) {
    return apiRun(
      () => supplierApi.update(supplierId, contacts, true),
      'Не удалось сохранить контакты',
    );
  },

  // ==== чаты ====

  /** «Мои чаты» поставщика по всем спецификациям (ТЗ 4.2, 6.10). */
  fetchChats(withPreloader = true) {
    return specChatApi.fetchMine(withPreloader).then((r) => r.items ?? []);
  },
};
